import React, { useContext, useCallback } from "react";
import { Box, Button, Grid } from "@mui/material";
import { MyDialog } from "../../../component/index";
import theme from "../../../styles/theme";
import { useDropzone } from "react-dropzone";
import DataSourceContext from "../../../context/DataSourceMngContext";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { Typography } from "@mui/material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import IconButton from "@mui/material/IconButton";

export default function DialogUploadFactSheet({ open, onClose }) {
  return (
    <MyDialog
      Header={`Upload Fact Sheet`}
      textConfirm="Save"
      maxHeight={600}
      minWidth={600}
      open={open}
      handleClose={onClose}
      actionContent={false}
    >
      <Contents onClose={onClose} />
    </MyDialog>
  );
}

const UploadFiles = () => {
  const { setDataState } = useContext(DataSourceContext);

  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        const reader = new FileReader();

        reader.onloadend = () => {
          if (reader.result) {
            setDataState((prev) => ({
              ...prev,
              files: [
                ...prev.files,
                {
                  file,
                  file_name: file.name,
                  file_type: file.type,
                  size: file.size,
                  lastModified: file.lastModified,
                  isActive: false,
                },
              ],
            }));
          }
        };
        reader.readAsDataURL(file);
      });
    },
    [setDataState]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/*": [".png", ".jpg", ".jpeg"],
      "application/pdf": [".pdf"],
    },
    onDrop,
    multiple: true, // ✅ รองรับหลายไฟล์
  });

  return (
    <Box
      sx={{
        width: "100%",
        height: "150px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        border: "1px dashed",
        borderColor: theme.palette.primary.dark,
        backgroundColor: isDragActive ? theme.palette.primary.light : "white",
        borderRadius: "10px",
        padding: 1,
        position: "relative",
        cursor: "pointer",
      }}
    >
      <Box
        {...getRootProps()}
        sx={{
          //   border: "1px dashed",
          borderColor: "#E0E3E7",
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          objectFit: "cover",
          borderRadius: "10px",
          flexDirection: "column",
        }}
      >
        <input {...getInputProps()} />
        <UploadOutlinedIcon
          sx={{ fontSize: 25, color: theme.palette.primary.dark }}
        />
        <Typography variant="h6">
          {isDragActive
            ? "Drop file(s) here..."
            : "Drag and drop file(s) here or click to upload"}
        </Typography>
      </Box>
    </Box>
  );
};

const FileItem = ({ file_type }) => {
  let displayType = file_type;
  let color = theme.palette.primary.main;
  if (file_type === "application/pdf") {
    displayType = "PDF";
    color = theme.palette.error.main;
  }
  if (file_type === "image/png") {
    displayType = "PNG";
    color = theme.palette.warning.main;
  }
  if (file_type === "image/jpeg") {
    displayType = "JPEG";
    color = "orange";
  }
  if (file_type === "image/jpg") {
    displayType = "JPG";
    color = "orange";
  }
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: 50,
        height: 50,
        position: "relative",
      }}
    >
      <InsertDriveFileOutlinedIcon
        sx={{ color: theme.palette.action.disabled, fontSize: 50 }}
      />
      <Typography
        sx={{
          fontSize: 8,
          color: "white",
          position: "absolute",
          top: 15,
          left: 3,
          backgroundColor: color,
          borderRadius: 0.5,
          padding: 0.5,
        }}
      >
        {displayType}
      </Typography>
    </Box>
  );
};

const FileList = ({ onClose }) => {
  const [view, setView] = React.useState("grid");
  const [selectedFiles, setSelectedFiles] = React.useState(false);
  const { files, setDataState, uploadTransaction } =
    useContext(DataSourceContext);

  const handleCheckbox = (index) => {
    const newFiles = files;
    newFiles[index].isActive = !newFiles[index].isActive;
    setDataState((prev) => ({ ...prev, files: newFiles }));
    // newFiles.filter((file) => file.isActive).length === 0 ? setSelectedFiles(false) : setSelectedFiles(true);
  };

  // const handleDelete = (index) => {
  //   const newFiles = files.filter((file) => file.isActive);
  //   setDataState((prev) => ({ ...prev, files: newFiles }));
  // };

  const handleDeleteFile = (index) => {
    const newFiles = files;
    newFiles[index].isActive = !newFiles[index].isActive;
    setDataState((prev) => ({
      ...prev,
      files: newFiles,
      warning: {
        ...prev.warning,
        open: true,
        severity: "error",
        warningTitle: "Confirm delete",
        warningText: null,
        handleOkClicked: () => {
          const newFiles = files.filter((file) => !file.isActive);
          setDataState((prev) => ({
            ...prev,
            files: newFiles,
            warning: {
              ...prev.warning,
              open: false,
            },
          }));
        },
        handleCancle: () =>
          setDataState((prev) => ({
            ...prev,
            warning: { ...prev.warning, open: false },
          })),
        okBtnText: "Delete",
      },
    }));
  };

  const handleDeleteAllFile = () => {
    setDataState((prev) => ({
      ...prev,
      warning: {
        ...prev.warning,
        open: true,
        warningTitle: "Confirm delete",
        warningText: null,
        severity: "error",
        handleOkClicked: () => {
          setDataState((prev) => ({
            ...prev,
            files: [],
            warning: { ...prev.warning, open: false },
          }));
        },
        handleCancle: () =>
          setDataState((prev) => ({
            ...prev,
            warning: { ...prev.warning, open: false },
          })),
        okBtnText: "Delete",
      },
    }));
  };

  const formatSize = (size) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  // const handleSelectAll = () => {
  //   if (files?.filter((f) => f.isActive)?.length === files?.length) {
  //       const newFiles = files.map((file) => ({ ...file, isActive: false }));
  //       setDataState((prev) => ({ ...prev, files: newFiles }));
  //   } else {
  //       const newFiles = files.map((file) => ({ ...file, isActive: true }));
  //       setDataState((prev) => ({ ...prev, files: newFiles }));
  //   }
  // };

  const handleCancel = () => {
    setDataState((prev) => ({
      ...prev,
      files: files.map((file) => ({ ...file, isActive: false })),
    }));
    setSelectedFiles(false);
  };

  //   const saveDelete = () => {
  //     const newFiles = files.filter((file) => !file.isActive);
  //     setDataState((prev) => ({
  //       ...prev,
  //       files: newFiles,
  //       warning: { ...prev.warning, open: false },
  //     }));
  //     setSelectedFiles(false);
  //   };

  //   const handleDelete = () => {
  //     setDataState((prev) => ({
  //       ...prev,
  //       warning: {
  //         ...prev.warning,
  //         open: true,
  //         warningTitle: "Confirm delete",
  //         warningText: null,
  //         handleOkClicked: saveDelete,
  //         handleCancle: () =>
  //           setDataState((prev) => ({
  //             ...prev,
  //             warning: { ...prev.warning, open: false },
  //           })),
  //         okBtnText: "Delete",
  //       },
  //     }));
  //   };

  const handleCancelUpload = () => {
    onClose();
  };

  const handleUpload = () => {
    setDataState((prev) => ({
      ...prev,
      loading: {
        ...prev.loading,
        modal: true,
      },
    }));
    uploadTransaction();
  };
  return (
    <Box>
      {files?.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
            padding: "10px 0px 6px 0px",
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: theme.palette.primary.dark,
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={handleDeleteAllFile}
            >
              Delete all
            </Typography>
            {/* {selectedFiles ? <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox size="small" checked={files?.filter((f) => f.isActive)?.length === files?.length} onChange={handleDeleteAllFile}/>
                    <Typography variant="h5" sx={{ color: theme.palette.primary.dark }}>Select all</Typography>
                </Box> : <Typography variant="h5" sx={{ color: theme.palette.primary.dark, margin: '10px 0px 0px 0px', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => files?.length > 0 && setSelectedFiles(true)}>Select files</Typography>} */}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {selectedFiles && (
              <Button variant="outlined" size="small" onClick={handleCancel}>
                Cancel
              </Button>
            )}
            {/* {files?.filter((f) => f.isActive)?.length > 0 &&
                <Button size='small' variant="contained" color='error' onClick={handleDelete}>Delete</Button>
              } */}
            <Box
              sx={{
                // display: "flex",
                // justifyContent: "center",
                // alignItems: "center",
                width: "50px",
                backgroundColor: "#f5f5f5",
                padding: "2px",
                borderRadius: "20px",
                border: "1px solid",
                borderColor: "#E0E3E7",
                cursor: "pointer",
              }}
              onClick={() => setView(view === "list" ? "grid" : "list")}
              //   className="translate-x-1"
            >
              <Box
                sx={{
                  transition: "all 0.2s ease",
                  transform:
                    view === "list" ? "translateX(0px)" : "translateX(20px)",
                }}
              >
                {view === "list" && (
                  <FormatListBulletedIcon
                    sx={{
                      color:
                        view === "list" ? "white" : theme.palette.primary.dark,
                      fontSize: 25,
                      backgroundColor:
                        view === "list" ? theme.palette.primary.dark : "white",
                      padding: "2px",
                      borderRadius: "20px",
                      border: "1px solid",
                      borderColor: "#E0E3E7",
                      cursor: "pointer",
                    }}
                    // onClick={() => setView("list")}
                  />
                )}
                {view === "grid" && (
                  <AppsOutlinedIcon
                    sx={{
                      color:
                        view === "grid" ? "white" : theme.palette.primary.dark,
                      fontSize: 25,
                      backgroundColor:
                        view === "grid" ? theme.palette.primary.dark : "white",
                      padding: "2px",
                      borderRadius: "20px",
                      border: "1px solid",
                      borderColor: "#E0E3E7",
                      cursor: "pointer",
                    }}
                    // onClick={() => setView("grid")}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* file lists */}
      {files?.length > 0 && (
        <Grid
          container
          spacing={1}
          sx={{
            maxHeight: "275px",
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: "4px",
            margin: "6px 0px",
          }}
        >
          {files.map((file, index) => {
            return (
              <Grid
                item
                xs={view === "list" ? 6 : 3}
                key={index}
                sx={{ padding: "8px 8px 0px 0px !important" }}
              >
                <Box
                  sx={{
                    border: "1px solid #E0E3E7",
                    padding: 1,
                    borderRadius: 2,
                    position: "relative",
                    display: "flex",
                    flexDirection: view === "list" ? "row" : "column",
                    // justifyContent:
                    //   view === "list" ? "space-between" : "center",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {/* {selectedFiles && (
                    <Checkbox
                      checked={file.isActive}
                      size="small"
                      sx={{
                        position: view === "grid" ? "absolute" : "relative",
                        top: 0,
                        left: 0,
                      }}
                      onChange={() => handleCheckbox(index)}
                    />
                  )} */}
                  <FileItem file_type={file?.file_type || null} />
                  <Box
                    sx={{
                      width: view === "list" ? "calc(100% - 60px)" : "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        // width: view === "list" ? 150 : 113,
                        maxWidth: "100%",
                      }}
                    >
                      {file.file_name}
                    </Typography>
                    <Typography sx={{ fontSize: 12 }}>
                      {file.size && formatSize(file.size)}
                    </Typography>
                    <Typography sx={{ fontSize: 12 }}>
                      {new Date(file.lastModified).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: -13,
                      right: -13,
                      padding: "4px",
                    }}
                  >
                    <CancelIcon
                      sx={{
                        color: theme.palette.error.main,
                        fontSize: 25,
                        cursor: "pointer",
                        backgroundColor: "white",
                        borderRadius: "50%",
                      }}
                      onClick={() => handleDeleteFile(index)}
                    />
                  </IconButton>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* btn action upload */}
      <Box
        sx={{
          width: "100%",
          position: "absolute",
          bottom: 0,
          left: 0,
          padding: "0px 20px",
        }}
      >
        {!selectedFiles && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              padding: 1,
              justifyContent: "center",
              borderTop: "1px solid #E0E3E7",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancelUpload}
              sx={{ width: "40%", height: "40px" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              sx={{ width: "40%", height: "40px" }}
              disabled={files.length === 0}
            >
              Confirm Upload{" "}
              {files?.length > 0 &&
                (files.length > 1
                  ? `${files.length} files`
                  : `${files.length} file`)}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const Contents = ({ onClose }) => {
  return (
    <Box sx={{ height: "90%" }}>
      <UploadFiles />
      <FileList onClose={onClose} />
    </Box>
  );
};
